import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Save, X, Send, TestTube, Link2Icon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from './input';
import { AIEmail, AILinkedInInvite } from '@prisma/client';
import Link from 'next/link';
import { StrategyAIEmail, StrategyAILinkedinInvite } from 'types/extend-prisma';

interface AIInviteSuggestionProps {
	invite: AILinkedInInvite | StrategyAILinkedinInvite;
	onSend?: (invite: AILinkedInInvite | StrategyAILinkedinInvite) => Promise<void>;
	onDiscard?: (invite: AILinkedInInvite | StrategyAILinkedinInvite) => Promise<void>;
	setInvite: (invite: AILinkedInInvite | StrategyAILinkedinInvite) => void;
	disabled?: boolean;
}
const AIInviteSuggestion: React.FC<AIInviteSuggestionProps> = ({
	invite,
	onSend,
	onDiscard,
	disabled,
	setInvite
}) => {
	const [isFocused, setIsFocused] = useState(false);
	const [isEditing, setIsEditing] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [initialInvite, setInitialInvite] = useState(JSON.parse(JSON.stringify(invite)));

	const isInviteEmpty = useMemo(() => {
		return !invite.subject?.trim() && !invite.body?.trim();
	}, [invite.subject, invite.body]);

	const handleChange = (field: keyof AILinkedInInvite, value: string) => {
		invite = { ...invite, [field]: value };
		setInvite(invite);
		setIsEditing(true);
	};

	const handleFocus = () => {
		setIsFocused(true);
		setIsEditing(true);
	};

	const handleBlur = () => {
		setIsFocused(false);
	};

	const handleUndo = () => {
		setIsLoading(true);
		invite.body = initialInvite.body;
		invite.subject = initialInvite.subject;
		setInvite(invite);
		setIsEditing(false);
		setIsLoading(false);
	};

	// Relevant for new disclaimers
	const handleAdd = () => {
		if (isInviteEmpty) return;
		setIsLoading(true);
		onSend?.(invite).then(() => {
			setIsEditing(false);
			setIsLoading(false);
			setInitialInvite(JSON.parse(JSON.stringify(invite)));
		});
	};

	const handleDiscard = () => {
		setIsLoading(true);
		onDiscard?.(invite).then(() => {
			setIsEditing(false);
			setIsLoading(false);
		});
	};

	return (
		<Card className={cn('w-full mb-6 transition-all duration-300', isFocused && 'shadow-xl')}>
			<CardHeader className="pb-4">
				<CardTitle>To {invite.linkedinUrl}</CardTitle>
				<div className="flex items-center space-x-2">
					<Link2Icon className="h-4 w-4" />
					<Link
						className="text-black-500 underline"
						href={`https://${invite.email.split('@').pop()}`}
						target="_blank"
					>
						{invite.email.split('@').pop()}
					</Link>
				</div>
				{invite.company_title && invite.company_description && (
					<div className="p-2 text-sm border-none rounded-md bg-muted">
						{invite.company_title && (
							<h4 className="font-semibold">{invite.company_title}</h4>
						)}
						{invite.company_description && <p>{invite.company_description}</p>}
						{invite.summary && (
							<p>
								<strong>About:{` `}</strong>
								{invite.summary}
							</p>
						)}
					</div>
				)}
			</CardHeader>
			<CardContent>
				<div className="grid gap-4 mt-2">
					<Input
						value={invite.subject ?? ''}
						onChange={(e: { target: { value: string } }) => {
							handleChange('subject', e.target.value);
						}}
						onFocus={handleFocus}
						onBlur={handleBlur}
						className="col-span-3"
						id="email-subject"
						placeholder="Message subject"
						disabled={isLoading}
					/>
					<Textarea
						value={invite.body ?? ''}
						onChange={(e: { target: { value: string } }) =>
							handleChange('body', e.target.value)
						}
						placeholder="Message body"
						id="email-content"
						disabled={isLoading}
						onFocus={handleFocus}
						onBlur={handleBlur}
						rows={10}
						className="col-span-3"
					/>
				</div>
				<div className="flex justify-end mt-4 gap-1 flex-wrap">
					{/* Resting State */}
					{!isEditing && !isInviteEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								onClick={handleAdd}
								disabled={isLoading || disabled}
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Send className="h-4 w-4 mr-2" />
								Send
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleDiscard}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Discard
							</Button>
						</>
					)}
					{!isEditing && isInviteEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								disabled
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Send className="h-4 w-4 mr-2" />
								Send
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleDiscard}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Discard
							</Button>
						</>
					)}
					{/* Active State */}
					{isEditing && !isInviteEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								onClick={handleAdd}
								disabled={isLoading || disabled}
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Send className="h-4 w-4 mr-2" />
								Send
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleUndo}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Undo
							</Button>
						</>
					)}
					{isEditing && isInviteEmpty && (
						<>
							<Button
								variant="outline"
								size="sm"
								disabled
								className="text-green-700 hover:bg-green-700 hover:text-white"
							>
								<Save className="h-4 w-4 mr-2" />
								Save
							</Button>
							<Button
								variant="outline"
								size="sm"
								onClick={handleUndo}
								disabled={isLoading}
							>
								<X className="h-4 w-4 mr-2" />
								Undo
							</Button>
						</>
					)}
				</div>
			</CardContent>
		</Card>
	);
};

export default AIInviteSuggestion;
